import type { TranscriptionRequest, TranscriptionResponse } from '@recipes/contracts';
import { LEVEL_WINDOW_MS } from './silence';

/** Minimal typing for the (still prefixed) Web Speech API. */
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
  length: number;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

export interface RecognizerHandlers {
  onInterim?: (text: string) => void;
  /** Microphone level (RMS, 0–1) about ten times a second; server adapter only. */
  onLevel?: (rms: number) => void;
  onFinal: (text: string) => void;
  onError: (error: Error) => void;
}

export interface Recognizer {
  readonly kind: 'browser' | 'server';
  start(): Promise<void>;
  /** Stop listening and deliver the final transcript. */
  stop(): Promise<void>;
  abort(): void;
}

export class SpeechUnavailableError extends Error {
  constructor(message = 'Speech recognition is not available') {
    super(message);
    this.name = 'SpeechUnavailableError';
  }
}

function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function browserRecognitionAvailable(): boolean {
  return speechRecognitionCtor() !== null;
}

export function mediaRecorderAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function toBcp47(language: 'de' | 'en'): string {
  return language === 'de' ? 'de-DE' : 'en-US';
}

/** Browser adapter (Chrome/Safari): interim results while the button is held. */
export function createBrowserRecognizer(
  language: 'de' | 'en',
  handlers: RecognizerHandlers,
): Recognizer {
  const Ctor = speechRecognitionCtor();
  if (!Ctor) throw new SpeechUnavailableError();
  const recognition = new Ctor();
  recognition.lang = toBcp47(language);
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  let finalText = '';
  let interimText = '';
  let ended: (() => void) | null = null;
  let errored = false;

  recognition.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i]!;
      const transcript = result[0].transcript;
      if (result.isFinal) finalText += `${transcript} `;
      else interim += transcript;
    }
    interimText = interim;
    handlers.onInterim?.(`${finalText}${interimText}`.trim());
  };
  recognition.onerror = (event) => {
    if (event.error === 'no-speech' || event.error === 'aborted') return;
    errored = true;
    handlers.onError(new Error(event.error));
  };
  recognition.onend = () => {
    if (!errored) handlers.onFinal(`${finalText}${interimText}`.trim());
    ended?.();
  };

  return {
    kind: 'browser',
    async start() {
      finalText = '';
      interimText = '';
      errored = false;
      recognition.start();
    },
    stop() {
      return new Promise<void>((resolve) => {
        ended = resolve;
        try {
          recognition.stop();
        } catch {
          resolve();
        }
      });
    },
    abort() {
      errored = true;
      try {
        recognition.abort();
      } catch {
        // ignore
      }
    },
  };
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // continue
    }
  }
  return '';
}

type AudioContextCtor = new () => AudioContext;

/** Measures the level of a stream on the device; returns the function that stops measuring. */
function startLevelMonitor(stream: MediaStream, onLevel: (rms: number) => void): () => void {
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return () => undefined;
  try {
    const context = new Ctor();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 4096;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    const timer = setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      onLevel(Math.sqrt(sum / samples.length));
    }, LEVEL_WINDOW_MS);
    return () => {
      clearInterval(timer);
      source.disconnect();
      void context.close().catch(() => undefined);
    };
  } catch {
    return () => undefined;
  }
}

export interface ServerRecognizerOptions {
  language: 'de' | 'en';
  context: TranscriptionRequest['context'];
  transcribe: (body: TranscriptionRequest) => Promise<TranscriptionResponse>;
}

/** Fallback adapter: record with MediaRecorder and transcribe on the server. */
export function createServerRecognizer(
  options: ServerRecognizerOptions,
  handlers: RecognizerHandlers,
): Recognizer {
  if (!mediaRecorderAvailable())
    throw new SpeechUnavailableError('Microphone recording unavailable');
  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let aborted = false;
  let stopLevels: (() => void) | null = null;

  const cleanup = () => {
    stopLevels?.();
    stopLevels = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    recorder = null;
  };

  return {
    kind: 'server',
    async start() {
      aborted = false;
      chunks = [];
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.start(250);
      if (handlers.onLevel) stopLevels = startLevelMonitor(stream, handlers.onLevel);
    },
    stop() {
      return new Promise<void>((resolve) => {
        const current = recorder;
        if (!current) {
          cleanup();
          resolve();
          return;
        }
        current.onstop = async () => {
          const mimeType = (current.mimeType || 'audio/webm').split(';')[0]!;
          const blob = new Blob(chunks, { type: mimeType });
          cleanup();
          if (aborted || blob.size === 0) {
            resolve();
            return;
          }
          try {
            const audioBase64 = await blobToBase64(blob);
            const response = await options.transcribe({
              audioBase64,
              mimeType,
              language: options.language,
              context: options.context,
            });
            handlers.onFinal(response.text.trim());
          } catch (error) {
            handlers.onError(error instanceof Error ? error : new Error(String(error)));
          }
          resolve();
        };
        try {
          current.stop();
        } catch {
          cleanup();
          resolve();
        }
      });
    },
    abort() {
      aborted = true;
      try {
        recorder?.stop();
      } catch {
        // ignore
      }
      cleanup();
    },
  };
}
